import { registerApi as api, globalApi } from '../../api';

const pickRegisterError = (error) => {
  const d = error.response?.data;
  if (typeof d?.message === 'object' && d.message?.error) return d.message.error;
  if (typeof d?.message === 'string') return d.message;
  if (typeof d?.error === 'string') return d.error;
  return error.response?.data?.error?.message || error.message;
};

const pickGlobalError = (error) => {
  const d = error.response?.data;
  if (typeof d?.error === 'string') return d.error;
  if (typeof d?.message === 'string') return d.message;
  return error.message || 'Request failed';
};

export const fetchCertificateTypes = async () => {
  try {
    const token = sessionStorage.getItem('token');
    const response = await globalApi.get('/get_certificate_type', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    throw pickGlobalError(error);
  }
};

export const createCertificateTypeApi = async (certificate_type) => {
  try {
    const token = sessionStorage.getItem('token');
    const response = await globalApi.post(
      '/certificate_type',
      { certificate_type },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    throw pickGlobalError(error);
  }
};

export const updateCertificateTypeApi = async (id, certificate_type) => {
  try {
    const token = sessionStorage.getItem('token');
    const response = await globalApi.put(
      `/update_certificate_type/${id}`,
      { certificate_type },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    throw pickGlobalError(error);
  }
};

export const deleteCertificateTypeApi = async (id) => {
  try {
    const token = sessionStorage.getItem('token');
    const response = await globalApi.delete(`/delete_certificates/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    throw pickGlobalError(error);
  }
};

export const multiDeleteCertificateTypesApi = async (ids) => {
  try {
    const token = sessionStorage.getItem('token');
    const response = await globalApi.post(
      '/delete_multiple_certificates',
      { ids },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    throw pickGlobalError(error);
  }
};

export const fetchSnECertificates = async (client_id, certificate_type = 'sne') => {
  try {
    const token = sessionStorage.getItem('token');
    const response = await api.get('/get_sne_certificates', {
      headers: { Authorization: `Bearer ${token}` },
      params: { client_id, certificate_type },
    });
    return response.data;
  } catch (error) {
    throw pickRegisterError(error);
  }
};

export const uploadSnECertificateApi = async ({
  file,
  client_id,
  unit_id,
  validity_expire_date,
  certificate_type = 'sne',
}) => {
  try {
    const token = sessionStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_id', client_id);
    formData.append('unit_id', unit_id);
    formData.append('validity_expire_date', validity_expire_date);
    formData.append('certificate_type', certificate_type);
    const response = await api.post('/upload_sne_certificate', formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw pickRegisterError(error);
  }
};

export const updateSnECertificateApi = async (id, { file, validity_expire_date }) => {
  try {
    const token = sessionStorage.getItem('token');
    const formData = new FormData();
    if (file) formData.append('file', file);
    if (validity_expire_date) formData.append('validity_expire_date', validity_expire_date);
    const response = await api.put(`/update_sne_certificate/${id}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw pickRegisterError(error);
  }
};

export const deleteSnECertificateApi = async (id) => {
  try {
    const token = sessionStorage.getItem('token');
    const response = await api.delete(`/delete_sne_certificate/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    throw pickRegisterError(error);
  }
};

const parseBlobError = async (error) => {
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      if (typeof parsed?.error === 'string') return parsed.error;
    } catch {
      // ignore parse errors
    }
  }
  return pickRegisterError(error);
};

export const downloadSnECertificateApi = async (id) => {
  try {
    const token = sessionStorage.getItem('token');
    const response = await api.get(`/download_sne_certificate/${id}`, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` },
    });

    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      const parsed = JSON.parse(text);
      throw parsed?.error || 'Download failed';
    }

    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;

    const disposition = response.headers['content-disposition'];
    let filename = 'sne_certificate.pdf';
    if (disposition && disposition.includes('filename=')) {
      filename = disposition.split('filename=')[1].replace(/['"]/g, '');
    }
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, status: response.status };
  } catch (error) {
    throw await parseBlobError(error);
  }
};

export const viewSnECertificateApi = async (id) => {
  try {
    const token = sessionStorage.getItem('token');
    const response = await api.get(`/view_sne_certificate/${id}`, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` },
    });

    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      const parsed = JSON.parse(text);
      throw parsed?.error || 'View failed';
    }

    return {
      success: true,
      blob: response.data,
      status: response.status,
    };
  } catch (error) {
    throw await parseBlobError(error);
  }
};
